import React, { useState } from 'react';
import FamilyListBar from './FamilyListBar';
import { getCurrentUser, logout } from '../services/api';
import { useCurrentList } from '../context/CurrentListContext';

interface MobileMenuProps {
  activeView: string;
  onViewChange: (view: string) => void;
  onAddItem?: () => void;
  onBuyAll?: () => void;
  onAddAllToBuy?: () => void;
  showShoppingActions?: boolean;
  bulkLoading?: boolean;
}

const MobileMenu: React.FC<MobileMenuProps> = ({
  activeView,
  onViewChange,
  onAddItem,
  onBuyAll,
  onAddAllToBuy,
  showShoppingActions = false,
  bulkLoading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isAdmin = getCurrentUser()?.role === 'admin';
  const { currentFamily, currentList } = useCurrentList();

  const views = [
    { id: 'dashboard', label: 'Tableau de bord', icon: '📊' },
    { id: 'list', label: 'Liste de courses', icon: '🛒' },
    { id: 'planner', label: 'Planificateur de repas', icon: '📅' }
  ];

  const mobileViews = views;

  const handleViewChange = (viewId: string) => {
    onViewChange(viewId);
    setIsOpen(false);
  };

  const handleAction = (actionFn?: () => void) => {
    if (actionFn) {
      actionFn();
      setIsOpen(false);
    }
  };

  return (
    <>
      {/* Mobile Header */}
      <div className="hidden max-md:flex items-center gap-3 px-4 py-3 bg-white shadow-soft mb-4 rounded-xl">
        <button
          type="button"
          className="flex-shrink-0 p-2 -ml-1 rounded-lg hover:bg-primary/10 transition-colors duration-200"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Menu"
        >
          <div className="flex flex-col gap-[3px] w-5 h-[15px] relative">
            <span className={`block h-0.5 w-full bg-gray-900 rounded-sm transition-all duration-300 ${isOpen ? 'rotate-45 translate-x-[5px] translate-y-[5px]' : ''}`} />
            <span className={`block h-0.5 w-full bg-gray-900 rounded-sm transition-all duration-300 ${isOpen ? 'opacity-0' : ''}`} />
            <span className={`block h-0.5 w-full bg-gray-900 rounded-sm transition-all duration-300 ${isOpen ? '-rotate-45 translate-x-[7px] -translate-y-[6px]' : ''}`} />
          </div>
        </button>

        <div className="min-w-0 flex-1">
          <div className="text-base font-semibold text-gray-900 leading-tight truncate">
            {views.find(v => v.id === activeView)?.label || 'Organisateur Familial'}
          </div>
          {activeView === 'list' && currentList && (
            <div className="text-xs text-gray-400 truncate">
              {currentFamily?.name} · {currentList.emoji} {currentList.name}
            </div>
          )}
        </div>
      </div>

      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 z-[998] transition-all duration-300 ${
          isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        onClick={() => setIsOpen(false)}
      />

      {/* Sidebar Menu */}
      <nav
        className={`fixed top-0 left-0 w-[280px] h-full bg-white z-[999] transition-transform duration-300 overflow-y-auto shadow-strong ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex justify-between items-center px-4 pt-6 pb-4 border-b border-gray-200 mb-4">
          <h2 className="text-xl font-semibold text-primary m-0">Organisateur Familial</h2>
          <button
            type="button"
            className="text-2xl text-gray-600 hover:bg-black/10 hover:text-gray-900 rounded transition-all duration-200 p-1"
            onClick={() => setIsOpen(false)}
          >
            ✕
          </button>
        </div>

        {/* Navigation Section */}
        <div className="mb-8 px-4">
          <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-4 mx-0 mt-0">
            Navigation
          </h3>
          {mobileViews.map((view) => (
            <button
              key={view.id}
              type="button"
              className={`flex items-center w-full border-none px-4 py-4 mb-2 rounded-lg cursor-pointer transition-all duration-200 text-left text-base ${
                activeView === view.id
                  ? 'bg-primary text-white'
                  : 'bg-transparent text-gray-900 hover:bg-primary/10'
              }`}
              onClick={() => handleViewChange(view.id)}
            >
              <span className="text-xl mr-4 w-6 text-center">{view.icon}</span>
              <span>{view.label}</span>
            </button>
          ))}
        </div>

        {/* Family & lists */}
        {activeView === 'list' && (
          <div className="mb-8 px-4">
            <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-4 mx-0 mt-0">
              Famille & listes
            </h3>
            <div onClick={(e) => e.stopPropagation()}>
              <FamilyListBar />
            </div>
          </div>
        )}

        {/* Actions Section */}
        {showShoppingActions && (
          <div className="mb-8 px-4">
            <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-4 mx-0 mt-0">
              Actions
            </h3>
            {onAddItem && (
              <button
                type="button"
                className="flex items-center w-full border-none px-4 py-4 mb-2 rounded-lg cursor-pointer transition-all duration-200 text-left text-base text-gray-900 hover:bg-success/10 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                onClick={() => handleAction(onAddItem)}
              >
                <span className="text-xl mr-4 w-6 text-center">➕</span>
                <span>Ajouter un article</span>
              </button>
            )}
            {onBuyAll && (
              <button
                type="button"
                className="flex items-center w-full border-none px-4 py-4 mb-2 rounded-lg cursor-pointer transition-all duration-200 text-left text-base text-gray-900 hover:bg-success/10 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                onClick={() => handleAction(onBuyAll)}
                disabled={bulkLoading}
              >
                <span className="text-xl mr-4 w-6 text-center">✅</span>
                <span>{bulkLoading ? 'Traitement...' : 'Tout acheter'}</span>
              </button>
            )}
            {onAddAllToBuy && (
              <button
                type="button"
                className="flex items-center w-full border-none px-4 py-4 mb-2 rounded-lg cursor-pointer transition-all duration-200 text-left text-base text-gray-900 hover:bg-success/10 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                onClick={() => handleAction(onAddAllToBuy)}
                disabled={bulkLoading}
              >
                <span className="text-xl mr-4 w-6 text-center">↩️</span>
                <span>{bulkLoading ? 'Traitement...' : 'Tout remettre'}</span>
              </button>
            )}
          </div>
        )}
        {/* Admin + logout */}
        <div className="px-4 mt-auto pt-4 border-t border-gray-100">
            {isAdmin && (
              <button
                type="button"
                className="flex items-center w-full border-none px-4 py-3 rounded-lg cursor-pointer transition-all duration-200 text-left text-sm text-gray-400 hover:bg-gray-50 hover:text-gray-600"
                onClick={() => { window.dispatchEvent(new Event('admin:open')); setIsOpen(false); }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-3">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
                <span>Administration</span>
              </button>
            )}
            <button
              type="button"
              className="flex items-center w-full border-none px-4 py-3 rounded-lg cursor-pointer transition-all duration-200 text-left text-sm text-gray-400 hover:bg-gray-50 hover:text-gray-600"
              onClick={logout}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-3">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              <span>Déconnexion</span>
            </button>
          </div>
      </nav>
    </>
  );
};

export default MobileMenu;